import { Injectable } from '@angular/core';

import { config } from '@config/config';

export interface Jwt {
    user_id: number;
    type: 'USER';
    scope: Array<string> | string;
    iat: number;
    exp: number;
    iss: string;
    sub: string;
}

@Injectable({
    providedIn: 'root',
})
export class JwtService {
    constructor() {}

    getTokenId(token: string): number {
        const jwt = this.decodeToken(token);
        return jwt[config.tokenIds[jwt.type]];
    }

    getScopes(token: string): string[] {
        const jwt = this.decodeToken(token);
        return typeof jwt.scope === 'string' ? [jwt.scope] : jwt.scope;
    }

    getBaseUrl(token: string): string {
        const jwt = this.decodeToken(token);
        return jwt ? `${config.baseUrls[jwt.type]}/${jwt.user_id}` : '';
    }

    decodeToken(token: string): Jwt {
        if (!token || token === '') {
            return;
        }

        const parts = token.split('.');

        if (parts.length !== 3) {
            throw new Error(
                "The inspected token doesn't appear to be a JWT." +
                    'Check to make sure it has three parts and see https://jwt.io for more.'
            );
        }

        const decoded = this.urlBase64Decode(parts[1]);
        if (!decoded) {
            throw new Error('Cannot decode the token.');
        }

        return JSON.parse(decoded);
    }

    getTokenExpirationDate(token: Jwt | string): Date | null {
        const decoded: Jwt = typeof token === 'string' ? this.decodeToken(token) : token;
        if (decoded === null) {
            return null;
        }
        if (!decoded.hasOwnProperty('exp')) {
            return null;
        }

        const date = new Date(0);
        date.setUTCSeconds(decoded.exp);

        return date;
    }

    isTokenExpired(token?: Jwt, offsetSeconds?: number): boolean {
        const date = this.getTokenExpirationDate(token);
        offsetSeconds = offsetSeconds || 0;

        if (date === null) {
            return true;
        }

        return !(date.valueOf() > new Date().valueOf() + offsetSeconds * 1000);
    }

    urlBase64Decode(str: string): string {
        let output = str.replace(/-/g, '+').replace(/_/g, '/');
        switch (output.length % 4) {
            case 0: {
                break;
            }
            case 2: {
                output += '==';
                break;
            }
            case 3: {
                output += '=';
                break;
            }
            default: {
                throw new Error('Illegal base64url string!');
            }
        }
        return this.b64DecodeUnicode(output);
    }

    // credits for decoder goes to https://github.com/atk
    private b64decode(str: string): string {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
        let output = '';

        str = String(str).replace(/=+$/, '');

        if (str.length % 4 === 1) {
            throw new Error('"atob" failed: The string to be decoded is not correctly encoded.');
        }

        for (
            // initialize result and counters
            let bc = 0, bs: any, buffer: any, idx = 0;
            // get next character
            (buffer = str.charAt(idx++));
            // character found in table? initialize bit storage and add its ascii value;
            // eslint-disable-next-line no-bitwise
            ~buffer &&
            ((bs = bc % 4 ? bs * 64 + buffer : buffer),
            // and if not first of each 4 characters,
            // convert the first 8 bits to one ascii character
            bc++ % 4)
                ? // eslint-disable-next-line
                  (output += String.fromCharCode(255 & (bs >> ((-2 * bc) & 6))))
                : 0
        ) {
            // try to find character in table (0-63, not found => -1)
            buffer = chars.indexOf(buffer);
        }
        return output;
    }

    private b64DecodeUnicode(str: any) {
        return decodeURIComponent(
            Array.prototype.map
                .call(this.b64decode(str), (c: any) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
    }
}
